// ============================================
// CAP.06 FD-S — Unit Mapping Management Admin UI
//
// Fail-closed behavior: queries CAP.10 for CRITICAL biomarker list at
// page load before rendering conversionFactor edit controls.
//
// If CAP.10 returns error or times out (5 seconds):
//   conversionFactor edit controls are replaced with a locked state.
//   Operator-visible error is shown. No bypass or admin override.
//   Read-only views remain accessible.
//
// Per-biomarker blocking: reads _ops/cap06_fds_state from Firestore.
//   If a biomarker is in blockedBiomarkers (daily cross-check found it
//   absent from CAP.10), that biomarker's edit controls are also blocked.
//
// Spec: specs/bones/captain/hlx-cap-06-unit-standardization.md §FD-S c1, c3
// ============================================

import { useState, useEffect } from "react";
import { Lock, AlertTriangle, RefreshCw, Shield } from "lucide-react";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../../../services/firebase";
import { CF_URLS } from "../../../config/api";
import { C_OPS, C_UNIT_DATABASE } from "../../../constants/collections";

// ── Types ──

interface Cap10CriticalBiomarker {
  canonicalName: string;
  weight: number;
}

interface UnitRecord {
  unitId: string;
  canonicalUnit: string;
  conversionFactor?: number | null;
  status: "active" | "deprecated";
  quarantined?: boolean;
  biomarkerName?: string;
}

interface FdsState {
  blockedBiomarkers: string[];
  status: string;
  updatedAt: string;
}

type Cap10LoadStatus = "loading" | "ok" | "error";

const CAP10_TIMEOUT_MS = 5000;
const FDS_STATE_DOC = "cap06_fds_state";

// ── Component ──

export function UnitMappingManagement() {
  const [cap10Status, setCap10Status] = useState<Cap10LoadStatus>("loading");
  const [cap10Error, setCap10Error] = useState<string | null>(null);
  const [fdsState, setFdsState] = useState<FdsState | null>(null);
  const [units, setUnits] = useState<UnitRecord[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(true);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  // ── Load CAP.10 with 5-second timeout ──
  useEffect(() => {
    let cancelled = false;

    async function loadCap10() {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), CAP10_TIMEOUT_MS);

      try {
        const response = await fetch(CF_URLS.biomarkersCriticalList, {
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`CAP.10 returned HTTP ${response.status}`);
        }

        const data = (await response.json()) as {
          biomarkers: Cap10CriticalBiomarker[];
        };

        if (!Array.isArray(data.biomarkers)) {
          throw new Error("CAP.10 response missing biomarkers array");
        }

        if (!cancelled) {
          setCap10Status("ok");
        }
      } catch (err) {
        clearTimeout(timeoutId);
        if (!cancelled) {
          const msg =
            err instanceof Error
              ? err.name === "AbortError"
                ? "CAP.10 request timed out after 5 seconds"
                : err.message
              : String(err);
          setCap10Status("error");
          setCap10Error(msg);
        }
      }
    }

    void loadCap10();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Load FDS blocked state from Firestore ──
  useEffect(() => {
    async function loadFdsState() {
      try {
        const snap = await getDoc(doc(db, C_OPS, FDS_STATE_DOC));
        if (snap.exists()) {
          setFdsState(snap.data() as FdsState);
        }
      } catch (err) {
        console.error("[UnitMapping] Failed to load FDS state:", err);
      }
    }
    void loadFdsState();
  }, []);

  // ── Load unit_database records ──
  useEffect(() => {
    async function loadUnits() {
      try {
        const snap = await getDocs(collection(db, C_UNIT_DATABASE));
        const records: UnitRecord[] = snap.docs.map((d) => ({
          unitId: d.id,
          ...(d.data() as Omit<UnitRecord, "unitId">),
        }));
        records.sort((a, b) => a.canonicalUnit.localeCompare(b.canonicalUnit));
        setUnits(records);

        // Seed edit values from current conversionFactor
        const initials: Record<string, string> = {};
        for (const unit of records) {
          if (unit.conversionFactor != null) {
            initials[unit.unitId] = String(unit.conversionFactor);
          }
        }
        setEditValues(initials);
      } catch (err) {
        console.error("[UnitMapping] Failed to load unit_database:", err);
      } finally {
        setLoadingUnits(false);
      }
    }
    void loadUnits();
  }, []);

  const blockedBiomarkers = new Set<string>(fdsState?.blockedBiomarkers ?? []);

  function isBiomarkerBlocked(unit: UnitRecord): boolean {
    if (!unit.biomarkerName) return false;
    return blockedBiomarkers.has(unit.biomarkerName);
  }

  // ── Save handler (placeholder — wires into conversionFactorIntegrityService) ──
  async function handleSave(unitId: string) {
    setSaving((s) => ({ ...s, [unitId]: true }));
    try {
      // In production: call writeConversionFactorHash via admin callable
      // For now: log intent — full write path wired via CAP.06 D#11
      console.log(
        `[UnitMapping] conversionFactor change queued for unitId=${unitId} ` +
          `newValue=${editValues[unitId]}`,
      );
    } finally {
      setSaving((s) => ({ ...s, [unitId]: false }));
    }
  }

  // ── Render: loading ──
  if (cap10Status === "loading") {
    return (
      <div className="flex items-center gap-3 py-12 text-text-secondary">
        <RefreshCw size={18} className="animate-spin" />
        <span className="text-sm">Verifying CRITICAL biomarker list availability…</span>
      </div>
    );
  }

  // ── Render: CAP.10 failed — locked state ──
  if (cap10Status === "error") {
    return (
      <div>
        <PageHeader />
        {/* Locked state — operator-visible, no bypass */}
        <div
          className="flex items-start gap-4 p-5 rounded-lg border-2 border-accent bg-red-50"
          role="alert"
          aria-live="assertive"
        >
          <Lock size={24} className="text-accent flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-accent">
              Unit mapping changes temporarily unavailable — CRITICAL biomarker list cannot be
              loaded. Contact engineering if this persists beyond 30 minutes.
            </p>
            {cap10Error && (
              <p className="text-xs text-text-muted mt-1 font-mono">{cap10Error}</p>
            )}
          </div>
        </div>

        {/* Read-only views remain accessible */}
        <div className="mt-6">
          <p className="text-xs text-text-muted mb-3 flex items-center gap-1.5">
            <Shield size={13} />
            Read-only view — edit controls locked until CRITICAL biomarker list is available
          </p>
          <UnitTable
            units={units}
            loading={loadingUnits}
            editValues={editValues}
            onEditChange={() => undefined}
            onSave={() => undefined}
            saving={{}}
            cap10Locked={true}
            blockedBiomarkers={blockedBiomarkers}
          />
        </div>
      </div>
    );
  }

  // ── Render: CAP.10 OK — edit controls available (subject to per-biomarker blocking) ──
  return (
    <div>
      <PageHeader />

      {/* FDS state warning if any biomarkers are blocked */}
      {blockedBiomarkers.size > 0 && (
        <div
          className="flex items-start gap-3 p-4 rounded-lg border border-amber-400 bg-amber-50 mb-4"
          role="alert"
        >
          <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">
              {blockedBiomarkers.size} biomarker(s) have blocked edits pending Dr. Calapai review
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              Affected: {[...blockedBiomarkers].join(", ")}
            </p>
          </div>
        </div>
      )}

      <UnitTable
        units={units}
        loading={loadingUnits}
        editValues={editValues}
        onEditChange={(unitId, val) =>
          setEditValues((prev) => ({ ...prev, [unitId]: val }))
        }
        onSave={handleSave}
        saving={saving}
        cap10Locked={false}
        blockedBiomarkers={blockedBiomarkers}
        isBiomarkerBlocked={isBiomarkerBlocked}
      />
    </div>
  );
}

// ── Sub-components ──

function PageHeader() {
  return (
    <div className="mb-6">
      <h2 className="text-2xl font-display text-text-primary">Unit Mapping Management</h2>
      <p className="text-sm text-text-secondary mt-1">
        ConversionFactor values for the unit_database. Edits require CAP.10 availability and
        Dr. Calapai sign-off.
      </p>
    </div>
  );
}

interface UnitTableProps {
  units: UnitRecord[];
  loading: boolean;
  editValues: Record<string, string>;
  onEditChange: (unitId: string, val: string) => void;
  onSave: (unitId: string) => Promise<void>;
  saving: Record<string, boolean>;
  cap10Locked: boolean;
  blockedBiomarkers: Set<string>;
  isBiomarkerBlocked?: (unit: UnitRecord) => boolean;
}

function UnitTable({
  units,
  loading,
  editValues,
  onEditChange,
  onSave,
  saving,
  cap10Locked,
  isBiomarkerBlocked,
}: UnitTableProps) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-text-secondary text-sm">
        <RefreshCw size={15} className="animate-spin" />
        Loading unit records…
      </div>
    );
  }

  if (units.length === 0) {
    return (
      <p className="text-sm text-text-muted py-8">
        No unit records found in unit_database.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-bg-tertiary">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-bg-card border-b border-bg-tertiary">
            <th className="text-left px-4 py-3 text-text-secondary font-medium">Unit ID</th>
            <th className="text-left px-4 py-3 text-text-secondary font-medium">Canonical Unit</th>
            <th className="text-left px-4 py-3 text-text-secondary font-medium">Biomarker</th>
            <th className="text-left px-4 py-3 text-text-secondary font-medium">Status</th>
            <th className="text-left px-4 py-3 text-text-secondary font-medium">
              ConversionFactor
            </th>
            <th className="text-left px-4 py-3 text-text-secondary font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {units.map((unit) => {
            const blocked =
              cap10Locked || (isBiomarkerBlocked ? isBiomarkerBlocked(unit) : false);
            const isSaving = saving[unit.unitId] ?? false;

            return (
              <tr
                key={unit.unitId}
                className="border-b border-bg-tertiary last:border-0 hover:bg-bg-elevated"
              >
                <td className="px-4 py-3 font-mono text-xs text-text-muted">
                  {unit.unitId}
                </td>
                <td className="px-4 py-3 font-mono text-xs">{unit.canonicalUnit}</td>
                <td className="px-4 py-3 text-xs text-text-secondary">
                  {unit.biomarkerName ?? "—"}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                      unit.status === "active"
                        ? "bg-green-100 text-green-700"
                        : "bg-bg-tertiary text-text-muted"
                    }`}
                  >
                    {unit.status}
                  </span>
                  {unit.quarantined && (
                    <span className="ml-1 inline-block px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-accent">
                      quarantined
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {blocked ? (
                    <div className="flex items-center gap-1.5 text-text-muted">
                      <Lock size={13} />
                      <span className="text-xs font-mono">
                        {unit.conversionFactor != null
                          ? String(unit.conversionFactor)
                          : "—"}
                      </span>
                    </div>
                  ) : (
                    <input
                      type="number"
                      step="any"
                      className="w-28 px-2 py-1 rounded border border-bg-tertiary bg-bg-card text-xs font-mono focus:outline-none focus:border-accent"
                      value={editValues[unit.unitId] ?? ""}
                      onChange={(e) => onEditChange(unit.unitId, e.target.value)}
                      aria-label={`ConversionFactor for ${unit.canonicalUnit}`}
                    />
                  )}
                </td>
                <td className="px-4 py-3">
                  {blocked ? (
                    <span className="text-xs text-text-muted flex items-center gap-1">
                      <Lock size={12} />
                      {cap10Locked ? "CAP.10 unavailable" : "Blocked pending review"}
                    </span>
                  ) : (
                    <button
                      onClick={() => void onSave(unit.unitId)}
                      disabled={isSaving}
                      className="px-3 py-1 rounded text-xs bg-accent text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isSaving ? "Saving…" : "Save"}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
